// Copyright (c) Microsoft. All rights reserved.

import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { FluentProvider, Subtitle1, makeStyles, shorthands, tokens } from '@fluentui/react-components';

import i18next from 'i18next';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AbtisConfig } from './abtis-config';
import { BackendProbe, ChatView, Error, Loading, Login } from './components/views';
import { AuthHelper } from './libs/auth/AuthHelper';
import { useChat, useFile } from './libs/hooks';
import { AlertType } from './libs/models/AlertType';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState } from './redux/app/store';
import { FeatureKeys } from './redux/features/app/AppState';
import { addAlert, setActiveUserInfo, setServiceInfo, toggleFeatureFlag } from './redux/features/app/appSlice';
import {
    Breakpoints,
    abtisAccentColorName,
    abtisAccentColorValueString,
    abtisAccentHoverColorName,
    semanticKernelDarkTheme,
    semanticKernelLightTheme,
} from './styles';

import * as msTeams from '@microsoft/teams-js';
import { useTeams } from '@microsoft/teamsfx-react';
import { DarkModeSwitch } from './components/header/DarkModeSwitch';
import { LanguageSwitch } from './components/header/LanguageSwitch';
import { SourceInfo } from './components/header/SourceInfo';
import { UserSettingsMenu } from './components/header/UserSettingsMenu';
import { PluginGallery } from './components/open-api-plugins/PluginGallery';
import { TeamsFxContext } from './internal/context';

export const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        ...shorthands.overflow('hidden'),
    },
    header: {
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground5,
        color: tokens.colorNeutralForeground1,
        display: 'flex',
        '& h1': {
            paddingLeft: tokens.spacingHorizontalXL,
            display: 'flex',
            ...Breakpoints.small({
                paddingLeft: '0px',
            }),
        },
        height: '48px',
        justifyContent: 'space-between',
        width: '100%',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        ...Breakpoints.small({
            flexDirection: 'column',
            height: '96px',
            justifyContent: 'center',
        }),
    },
    persona: {
        marginRight: tokens.spacingHorizontalXXL,
    },
    cornerItems: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'flex-end',
        ...shorthands.gap(tokens.spacingHorizontalS),
        ...Breakpoints.small({
            ...shorthands.borderBottom(tokens.strokeWidthThin, ' solid', tokens.colorBrandStroke1),
            justifyContent: 'space-between',
            width: '100%',
        }),
    },
    companyName: {
        color: abtisAccentColorValueString,
    },
});

enum AppState {
    ProbeForBackend,
    SettingUserInfo,
    ErrorLoadingChats,
    ErrorLoadingUserInfo,
    LoadingChats,
    Chat,
    SigningOut,
}

interface AppProps {
    inTeams: boolean;
}

export const App = (props: AppProps) => {
    const context = useTeams()[0].context;
    const contextTheme = useTeams()[0].themeString;

    const classes = useClasses();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const portalName = useSelector((state: RootState) => state.config.configValues.portalName);

    const accentColor: string = useSelector((state: RootState) => state.config.configValues.reactAppAccentColor);
    const accentColorOnHover: string = useSelector(
        (state: RootState) => state.config.configValues.reactAppAccentColorOnHover,
    );
    const accentColorDarkmode: string = useSelector(
        (state: RootState) => state.config.configValues.reactAppAccentColorDarkmode,
    );
    const accentColorOnHoverDarkmode: string = useSelector(
        (state: RootState) => state.config.configValues.reactAppAccentColorOnHoverDarkmode,
    );

    const teamsUserCredential = useSelector((state: RootState) => state.teams.teamsValues.teamsUserCredential);

    const [tenantId, setTenantId] = useState('');
    const [objectId, setObjectId] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userInfoId, setUserInfoId] = useState('');
    const [userName, setUserName] = useState('');
    const [appState, setAppState] = React.useState(AppState.ProbeForBackend);

    const [darkModeEnabled, setDarkModeEnabled] = React.useState(false);

    const { instance, inProgress } = useMsal();
    const { features, isMaintenance } = useAppSelector((state: RootState) => state.app);
    const isAuthenticated = useIsAuthenticated();

    const chat = useChat();
    const file = useFile();

    useEffect(() => {
        async function fetchTeamsInfo() {
            if (teamsUserCredential) {
                const teamsUserInfo = await teamsUserCredential.getUserInfo();
                setTenantId(teamsUserInfo.tenantId);
                setObjectId(teamsUserInfo.objectId);
                setUserEmail(teamsUserInfo.preferredUserName);
                setUserInfoId(`${teamsUserInfo.objectId}.${teamsUserInfo.tenantId}`);
            }
        }
        void fetchTeamsInfo();

        if (props.inTeams) {
            if (context && typeof context.app.locale === 'string') {
                // Handle the Promise returned by i18next.changeLanguage
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                i18next.changeLanguage(context.app.locale).catch((error) => {
                    console.error('Failed to change language:', error);
                });
            }
        }

        handleThemeChange(contextTheme);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.inTeams, context, teamsUserCredential]);

    useEffect(() => {
        if (darkModeEnabled) {
            //this is the only way to set custom colors during runtime, as Griffel, which is used for css-styling in this project, performs ahead-of-time compilation values only
            // https://griffel.js.org/react/guides/limitations/
            document.documentElement.style.setProperty(abtisAccentColorName, accentColorDarkmode);
            document.documentElement.style.setProperty(abtisAccentHoverColorName, accentColorOnHoverDarkmode);
        } else {
            document.documentElement.style.setProperty(abtisAccentColorName, accentColor);
            document.documentElement.style.setProperty(abtisAccentHoverColorName, accentColorOnHover);
        }
    }, [accentColor, accentColorDarkmode, accentColorOnHover, accentColorOnHoverDarkmode, darkModeEnabled]);

    useEffect(() => {
        if (props.inTeams) {
            msTeams.app.registerOnThemeChangeHandler(handleThemeChange);
        } else {
            const darkModeEnabledStorageValue = localStorage.getItem(
                AbtisConfig.localStorageKeys.abtisChatbotDarkmodeEnabledName,
            ) as string;
            if (darkModeEnabledStorageValue) {
                const darkModeEnabledStorageBool = JSON.parse(darkModeEnabledStorageValue) as boolean;
                changeDarkMode(darkModeEnabledStorageBool);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeDarkMode, contextTheme, props.inTeams]);

    useEffect(() => {
        if (darkModeEnabled !== features[FeatureKeys.DarkMode].enabled) {
            setDarkModeEnabled(!darkModeEnabled);
        }
        // including darkModeEnabled in the dependency array causes an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [features]);

    useEffect(() => {
        if (isMaintenance && appState !== AppState.ProbeForBackend) {
            setAppState(AppState.ProbeForBackend);
            return;
        }

        if (!props.inTeams) {
            if (isAuthenticated && appState === AppState.SettingUserInfo) {
                const account = instance.getActiveAccount();
                if (!account) {
                    setAppState(AppState.ErrorLoadingUserInfo);
                } else {
                    setUserInfoId(`${account.localAccountId}.${account.tenantId}`);
                    setUserEmail(account.username); // username in an AccountInfo object is the email address
                    setUserName(account.name ?? account.username);

                    // Privacy disclaimer for internal Microsoft users
                    if (account.username.split('@')[1] === 'microsoft.com') {
                        dispatch(
                            addAlert({
                                message:
                                    'By using Chat Copilot, you agree to protect sensitive data, not store it in chat, and allow chat history collection for service improvements. This tool is for internal use only.',
                                type: AlertType.Info,
                            }),
                        );
                    }

                    setAppState(AppState.LoadingChats);
                }
            }
        }

        const isAuthValid = isAuthenticated || !AuthHelper.isAuthAAD() || props.inTeams;

        if (isAuthValid && appState === AppState.LoadingChats) {
            void Promise.all([
                // Load all chats from memory
                chat
                    .loadChats()
                    .then(() => {
                        setAppState(AppState.Chat);
                    })
                    .catch(() => {
                        setAppState(AppState.ErrorLoadingChats);
                    }),

                // Check if content safety is enabled
                file.getContentSafetyStatus(),

                // Load service information
                chat
                    .getServiceInfo()
                    .then((serviceInfo) => {
                        if (serviceInfo) {
                            dispatch(setServiceInfo(serviceInfo));
                        }
                    })
                    .catch((error) => {
                        console.log('error calling chat.getUserInfo', error);
                    }),
            ]);
        }

        dispatch(
            setActiveUserInfo({
                id: userInfoId,
                email: userEmail,
                username: userName,
            }),
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, inProgress, isAuthenticated, appState, objectId, tenantId, isMaintenance]);

    useEffect(() => {
        document.title = `${portalName} | Chat Copilot`;
    }, [portalName]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    function changeDarkMode(darkModeEnabledStorageBool: boolean) {
        setDarkModeEnabled(darkModeEnabledStorageBool);
        if (darkModeEnabledStorageBool !== features[FeatureKeys.DarkMode].enabled) {
            dispatch(toggleFeatureFlag(FeatureKeys.DarkMode));
        }
    }

    function handleThemeChange(theme: string) {
        if (theme === 'dark' || theme === 'contrast') {
            setDarkModeEnabled(true);
        } else {
            setDarkModeEnabled(false);
        }
    }

    const content = (
        <Chat
            classes={classes}
            appState={appState}
            setAppState={setAppState}
            portalName={portalName}
            inTeams={props.inTeams}
        />
    );
    return (
        <>
            {!props.inTeams && (
                <FluentProvider
                    className="app-container"
                    theme={darkModeEnabled ? semanticKernelDarkTheme : semanticKernelLightTheme}
                >
                    {AuthHelper.isAuthAAD() ? (
                        <>
                            <UnauthenticatedTemplate>
                                <div className={classes.container}>
                                    <div className={classes.header}>
                                        <Subtitle1 as="h1">
                                            <span className={classes.companyName}>{portalName}</span>
                                            &nbsp;| Chat Copilot
                                        </Subtitle1>
                                    </div>
                                    {appState === AppState.SigningOut && <Loading text={t('SignOutText')} />}
                                    {appState !== AppState.SigningOut && <Login />}
                                </div>
                            </UnauthenticatedTemplate>
                            <AuthenticatedTemplate>{content}</AuthenticatedTemplate>
                        </>
                    ) : (
                        content
                    )}
                </FluentProvider>
            )}
            {props.inTeams && (
                <TeamsFxContext.Provider
                    value={{ setTenantId, setObjectId, setUserEmail, objectId, userEmail, tenantId }}
                >
                    <FluentProvider
                        className="app-container"
                        theme={darkModeEnabled ? semanticKernelDarkTheme : semanticKernelLightTheme}
                    >
                        <Chat
                            classes={classes}
                            appState={appState}
                            setAppState={setAppState}
                            portalName={portalName}
                            inTeams={props.inTeams}
                        />
                    </FluentProvider>
                </TeamsFxContext.Provider>
            )}
        </>
    );
};

const Chat = ({
    classes,
    appState,
    setAppState,
    portalName,
    inTeams,
}: {
    classes: ReturnType<typeof useClasses>;
    appState: AppState;
    setAppState: (state: AppState) => void;
    portalName: string;
    inTeams: boolean;
}) => {
    const { t } = useTranslation();

    const onBackendFound = React.useCallback(() => {
        if (inTeams) {
            setAppState(AppState.LoadingChats);
        } else {
            setAppState(
                AuthHelper.isAuthAAD()
                    ? // if AAD is enabled, we need to set the active account before loading chats
                      AppState.SettingUserInfo
                    : // otherwise, we can load chats immediately
                      AppState.LoadingChats,
            );
        }
    }, [inTeams, setAppState]);

    return (
        <>
            {!inTeams && (
                <div className={classes.container}>
                    <div className={classes.header}>
                        <Subtitle1 as="h1">
                            <span className={classes.companyName}>{portalName}</span>
                            &nbsp;| Chat Copilot
                        </Subtitle1>
                        {appState > AppState.SettingUserInfo && (
                            <div className={classes.cornerItems}>
                                <div className={classes.cornerItems}>
                                    {AbtisConfig.featureFlags.showPluginGallery && <PluginGallery />}
                                    <LanguageSwitch />
                                    <DarkModeSwitch />
                                    <SourceInfo />
                                    <UserSettingsMenu
                                        setLoadingState={() => {
                                            setAppState(AppState.SigningOut);
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    {appState === AppState.ProbeForBackend && <BackendProbe onBackendFound={onBackendFound} />}
                    {appState === AppState.SettingUserInfo && (
                        <Loading text={'Hang tight while we fetch your information...'} />
                    )}
                    {appState === AppState.ErrorLoadingUserInfo && (
                        <Error text={'Unable to load user info. Please try signing out and signing back in.'} />
                    )}
                    {appState === AppState.ErrorLoadingChats && (
                        <Error text={'Unable to load chats. Please try refreshing the page.'} />
                    )}
                    {appState === AppState.LoadingChats && <Loading text={t('LoadingChatsText')} />}
                    {appState === AppState.Chat && <ChatView />}
                </div>
            )}

            {inTeams && (
                <div className={classes.container}>
                    {appState === AppState.ProbeForBackend && <BackendProbe onBackendFound={onBackendFound} />}
                    {appState === AppState.SettingUserInfo && (
                        <Loading text={'Hang tight while we fetch your information...'} />
                    )}
                    {appState === AppState.ErrorLoadingUserInfo && (
                        <Error text={'Unable to load user info. Please try signing out and signing back in.'} />
                    )}
                    {appState === AppState.ErrorLoadingChats && (
                        <Error text={'Unable to load chats. Please try refreshing the page.'} />
                    )}
                    {appState === AppState.LoadingChats && <Loading text={t('LoadingChatsText')} />}
                    {appState === AppState.Chat && <ChatView />}
                </div>
            )}
        </>
    );
};

export default App;
